import { h, Component } from 'preact';
import Store from 'store/store';

import { getRelativePageUrl, getValueForClientSize } from 'utils/bolt';
import { filterUnusedPages } from 'utils/bolt/header';
import { useDynamicAssetFolder } from 'utils/assets';
import { sortObjectByOrder, accessNested, joinClasses } from 'utils/utils';
import * as webp from 'utils/bolt/webp';

import HTML from 'components/core/html/html';
import Socials from 'components/bolt/socials/socials';
import Link from 'components/bolt/link/link';

import s from 'components/bolt/header/headerExpanded.sss';

export default class HeaderExpanded extends Component {

	constructor(props) {
		super(props);

		this.closeAndNavigate = this.closeAndNavigate.bind(this);
		this.getFallbackFormat = this.getFallbackFormat.bind(this);
		this.updateLinks(props);
		this.isSplitHeader = !!this.featuredLinks.length;

		this.updateLinks = this.updateLinks.bind(this);
	}

	componentDidMount() {
		Store.on('REFRESH_HEADER_LINKS', this.updateLinks);
	}
	componentWillUnmount() {
		Store.off('REFRESH_HEADER_LINKS', this.updateLinks);
	}

	updateLinks(props = this.props) {
		this.navItems = sortObjectByOrder(accessNested(props, 'appData.header.powsterHeader.nav'));
		this.filteredNavItems = filterUnusedPages(props, this.navItems);
		this.standardLinks = this.filteredNavItems.filter(item => item && item.linkType === 'primary');
		this.featuredLinks = this.navItems.filter(item => item && item.linkType === 'secondary');
	}

	trackNavLink(linkName, id) {
		Store.emit('TRACK_NAV_LINK_CLICK', linkName, id);
	}

	getFallbackFormat() {
		return accessNested(this.props, 'appData.header.alternativeImageFormat', 'png');
	}

	closeAndNavigate(id, e, linkName, queryString) {
		this.updateSelectedShowtimesTab(id, queryString);

		this.trackNavLink(linkName, id);
		Store.emit('CLOSE_AND_NAVIGATE', id, e, {
			queryString,
			calendarRemindMode: this.props.isCalendarRemindMode
		});
	}

	updateSelectedShowtimesTab = (id, queryString) => {
		const activePage = this.props.activePage || 'boltHome';
		if (activePage !== 'boltShowtimes')
			return;

		const groupBookingsEnabled = accessNested(this.props, 'appData.pages.boltShowtimes.data.groupBookings.isEnabled', false);
		if (groupBookingsEnabled && id === 'boltShowtimes' && queryString === '?groupbookings') {
			Store.emit('UPDATE_SHOWTIMES_ACTIVE_TAB', 'Group Bookings', true);
			Store.get().set({ groupBookingsShowtimes: true });
		} else if (groupBookingsEnabled && id === 'boltShowtimes' && queryString === '?booknow') {
			Store.emit('UPDATE_SHOWTIMES_ACTIVE_TAB', 'Book Now', true);
			Store.get().set({ groupBookingsShowtimes: false });
		}
	}

	getLinkSizing(isLinkOut) {
		if (isLinkOut) {
			return null;
		}

		return this.isSplitHeader ? s.h4 : s.h3;
	}

	renderLink(element, isFeatured) {
		// const isInternalLink = /^bolt/.test(element.path);
		let [pageIdRaw, hash] = element.path?.split('#') || [];
		let [pageId, queryStringRaw] = pageIdRaw?.split('?') || [];
		let queryString = element.queryString || (queryStringRaw ? `?${queryStringRaw}` : '');

		const page = accessNested(this.props, ['appData', 'pages', pageId]);
		const calendarEnabled = !!accessNested(this.props, 'appData.header.powsterHeader.calendars', null);
		const isInternalLink = !!page || element.path?.startsWith('@');
		const sizing = this.getLinkSizing(!isInternalLink);

		const activePage = this.props.activePage || 'boltHome';
		const activeSection = this.props.headerData.activeSection || 'boltHome';
		let active = activePage === pageId || (activePage === 'boltHome' && activeSection === element.path);
		const baseCopy = accessNested(this.props, 'appData.copy.baseCopy', {});
		let content = baseCopy[element.name] || element.name || element.id;

		// - start  - groupBookings same page 2 header items active 'hack'
		const groupBookingsEnabled = accessNested(this.props, 'appData.pages.boltShowtimes.data.groupBookings.isEnabled', false);
		if (groupBookingsEnabled && activePage == pageId && element.path === 'boltShowtimes' && queryString === '?groupbookings') {
			active = Store.get().groupBookingsShowtimes === true;
		}
		if (groupBookingsEnabled && activePage == pageId && element.path === 'boltShowtimes' && queryString === '?booknow') {
			active = Store.get().groupBookingsShowtimes === false;
		}
		//- end -

		if (page?.ageRestricted && this.props.client?.ofAge === false) {
			return;
		}

		if (element.path === 'boltShowtimes' && this.props.isCalendarRemindMode) {
			if (!calendarEnabled) return;
			content = baseCopy.$MENU_CALENDAR_REMINDER || 'Reminder';
		}

		let attributes = {};
		if (!element.disabled) {
			if (!isInternalLink) {
				attributes.href = element.path;
				attributes.target = element.sameWindow ? '_self' : '_blank';
				attributes.rel = 'noopener noreferrer';
				attributes.onClick = () => this.trackNavLink(content);
			} else {
				attributes.href = getRelativePageUrl(pageId, queryString, hash) || '#';
				attributes.onClick = element.onClick || (e => this.closeAndNavigate(pageId + (hash ? `#${hash}` : ''), e, content, queryString));
			}
		} else {
			attributes['aria-disabled'] = true;
		}

		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');
		const font = isFeatured ? fontStyles.navSecondaryLink : fontStyles.navPrimaryLink;

		return (
			<Link
				key={element.id || element.link || element.name}
				class={joinClasses(s.link, active && s.active, sizing, font)}
				{...attributes}
			>
				<HTML class={s.content} content={content} markdown />
				<HTML class={s.hidden} content={content} aria-hidden markdown />
			</Link>
		);
	}

	renderRegionSelector() {
		const regionData = accessNested(this.props, 'appData.meta.region') || {};
		const regionSelectorCustomisation = accessNested(this.props, 'appData.header.powsterHeader.regionSelector.extraRegions', {});
		const currentRegionNameOverride = regionSelectorCustomisation[regionData.slug] || {};
		let currentRegionLabel = currentRegionNameOverride.label || regionData.slug;
		const useLanguage = this.props?.appData?.header?.powsterHeader?.regionSelector?.useLanguage;
		if (useLanguage) {
			currentRegionLabel = currentRegionNameOverride.label || regionData.languageFull || regionData.language;
		}
		const accessibilityCopy = accessNested(this.props.appData, 'copy.accessibilityCopy');
		return (
			<button
				class={s.regionSelectorButton}
				onClick={this.props.regionSelectorClick}
				key="regionSelectorButton"
				ariaLabel={accessibilityCopy.$REGION_SELECTOR_BTN_ARIA}
			>
				<div class={s.icon} />
				<span class={s.name}>{currentRegionLabel}</span>
			</button>
		);
	}

	renderSocial() {
		const socials = accessNested(this.props, 'appData.header.powsterHeader.follow');
		if (!socials) {
			return;
		}
		return (
			<Socials
				class={s.socials}
				socials={socials}
				navOpen={this.props.navOpen}
				key="socials"
			/>
		);
	}

	getTTSize(tt) {
		if (tt?.titleTreatmentSize?.desktop || tt?.titleTreatmentSize?.tablet || tt?.titleTreatmentSize?.mobile) {
			return getValueForClientSize(tt.titleTreatmentSize);
		}
	}

	getBackgroundElement(background) {
		const resolution = this.props.client?.deviceSize === 'mobile' ? 'mobile' : 'desktop';
		const assetObj = background?.[resolution];
		const bgPosition = background?.[resolution]?.bgPosition || 'center';

		if (!assetObj) {
			return null;
		}

		const asset = assetObj.asset;
		const fallbackImg = assetObj.fallbackImg;
		const preLoadImg = assetObj.preLoadImg;
		const isVideo = asset?.endsWith('.mp4') || asset?.endsWith('.webm');

		this.state = { videoLoaded: false };

		const handleVideoLoad = () => {
			this.setState({ videoLoaded: true });
		};

		if (isVideo) {
			return (
				<div class={s.backgroundContainer}>
					{preLoadImg && !this.state.videoLoaded && (
						<img
							class={s.preLoadImage}
							src={preLoadImg}
							alt="Preload Background"
							style={{ objectPosition: bgPosition }}
						/>
					)}
					<video
						class={s.backgroundVideo}
						src={asset}
						autoPlay
						loop
						muted
						playsInline
						poster={fallbackImg}
						style={{ objectPosition: bgPosition }}
						onLoadedData={handleVideoLoad}
					/>
				</div>
			);
		} else if (asset) {
			return (
				<img
					class={s.backgroundImage}
					src={asset}
					alt="Background"
					style={{ backgroundPosition: bgPosition }}
				/>
			);
		} else if (fallbackImg) {
			return (
				<img
					class={s.backgroundImage}
					src={fallbackImg}
					alt="Fallback Background"
					style={{ backgroundPosition: bgPosition }}
				/>
			);
		}
		return null;
	}

	renderTT() {
		const tt = accessNested(this.props, 'appData.header.powsterHeader.tt', null);
		const deviceSize = accessNested(this.props, 'client.deviceSize');
		let deviceBreakpoint = deviceSize;
		if (deviceSize !== 'mobile' && deviceSize !== 'tablet') {
			deviceBreakpoint = 'desktop';
		}
		if (!tt || !(deviceBreakpoint in tt)) return;

		const ttSize = this.getTTSize(tt);
		let ttStyle = ttSize && {
			width: '100%',
			height: '100%',
			maxWidth: ttSize.maxWidth,
			maxHeight: ttSize.maxHeight
		};
		const copy = accessNested(this.props, 'appData.copy');
		const fallbackFormat = this.getFallbackFormat();

		// TODO: get rid of webp.Picture and just use whatever asset is specified in thundr (webp is widely supported)
		return (
			<webp.Picture
				style={ttStyle}
				class={s.tt}
				src={useDynamicAssetFolder(getValueForClientSize(tt))}
				alt={copy.accessibilityCopy[tt.alt] || tt.alt}
				fallbackFormat={fallbackFormat}
			/>
		);
	}

	renderStandardLinks() {
		return (
			<div class={s.standard} key="standard">
				{this.renderTT()}
				{this.standardLinks.map(link => this.renderLink(link, false))}
			</div>
		);
	}

	renderFeaturedLinks() {
		if (!this.isSplitHeader) return;
		return (
			<div class={s.featured} key="featured">
				{this.featuredLinks.map(link => this.renderLink(link, true))}
			</div>
		);
	}

	renderLinks() {
		const groups = [this.renderStandardLinks(), this.renderFeaturedLinks()].filter(Boolean);
		return groups.flatMap((g, i) => [i > 0 && <div class={s.separator} />, g]);
	}

	render({ navOpen, showRegionSelector }) {
		const store = Store.get();
		let { pushDown, cookieBannerOpen, height: cookieHeight } = store.cookies || {};
		const externalBannerOpen = accessNested(store, 'externalCookieBanner.bannerOpen');
		const bannerPushDown = externalBannerOpen || (pushDown && cookieBannerOpen);
		const sonyHeaderEnabled = accessNested(this.props, 'appData.options.sonyFeatures.sonyHeader', false);
		const bannerPushDownMobile = bannerPushDown && !accessNested(store, 'client.isDesktopSize', false);
		const isSplitHeader = this.isSplitHeader;

		if (bannerPushDown) {
			cookieHeight = accessNested(store, 'externalCookieBanner.height');
		}

		const totalLinks = this.standardLinks.length + this.featuredLinks.length;

		const background = accessNested(this.props, 'appData.header.powsterHeader.bg');
		const backgroundElement = this.getBackgroundElement(background);

		return (
			<div
				class={joinClasses({
					s,
					container: true,
					open: navOpen,
					splitHeader: isSplitHeader,
					hasRegionSelector: showRegionSelector,
					hasManyLinks: totalLinks >= 8,
					bannerPushDown,
					sonyHeader: sonyHeaderEnabled,
				})}
				style={cookieHeight ? `--cookie-height: ${cookieHeight}px` : ''}
				body-scroll-lock-ignore={bannerPushDownMobile}
				inert={!navOpen}
			>
				{navOpen && backgroundElement}
				<div class={s.linksContainer}>
					<div class={s.links} role="navigation">
						{this.renderLinks()}
						{this.renderSocial()}
					</div>
					{this.renderRegionSelector()}
				</div>
			</div>
		);
	}

}
